// ---------------------------------------------------------------------------
// Shared ApexCharts configuration factory
// ---------------------------------------------------------------------------

/**
 * Named colour tokens used across all chart types.
 */
export const CHART_COLORS = {
  primary: "#6366f1",
  success: "#10b981",
  warning: "#f97316",
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#a855f7",
  gray: "#6b7280",
  pink: "#ec4899",
};

/**
 * Ordered palette for multi-series charts (8 colours).
 */
export const CHART_PALETTE = [
  CHART_COLORS.primary,
  CHART_COLORS.success,
  CHART_COLORS.warning,
  CHART_COLORS.danger,
  CHART_COLORS.info,
  CHART_COLORS.purple,
  CHART_COLORS.gray,
  CHART_COLORS.pink,
];

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` when the root `<html>` element has the `dark` class.
 * Called fresh on every chart render so theme changes are detected.
 */
export const isDarkMode = () =>
  document.documentElement.classList.contains("dark");

/**
 * Returns grid, label and tooltip colours matching the active theme.
 */
export const getThemeColors = () => {
  const dark = isDarkMode();

  return {
    grid: dark ? "#374151" : "#f3f4f6",
    labels: dark ? "#9ca3af" : "#6b7280",
    tooltip: "dark",
  };
};

// ---------------------------------------------------------------------------
// Base config
// ---------------------------------------------------------------------------

/**
 * Common ApexCharts options shared by every chart type.
 * Hides toolbar, enables smooth 800 ms animations, and applies theme colours.
 */
export const baseChartConfig = () => {
  const theme = getThemeColors();

  return {
    chart: {
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    grid: {
      borderColor: theme.grid,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      labels: { style: { colors: theme.labels, fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: theme.labels, fontSize: "12px" } },
    },
    tooltip: { theme: theme.tooltip },
    responsive: [
      {
        breakpoint: 640,
        options: {
          chart: { height: 260 },
          xaxis: { labels: { style: { fontSize: "10px" } } },
          yaxis: { labels: { style: { fontSize: "10px" } } },
        },
      },
    ],
  };
};

// ---------------------------------------------------------------------------
// Bar chart (pill-shaped gradient)
// ---------------------------------------------------------------------------

/**
 * Pill-shaped bar chart with vertical gradient fill and top data labels.
 */
export const barChartConfig = (series, categories, overrides = {}) => {
  const theme = getThemeColors();
  const base = baseChartConfig();

  const config = {
    ...base,
    chart: {
      ...base.chart,
      type: "bar",
    },
    plotOptions: {
      bar: {
        borderRadius: 20,
        borderRadiusApplication: "around",
        columnWidth: "45%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => {
        if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
        if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}k`;

        return val;
      },
      offsetY: -25,
      style: {
        fontSize: "12px",
        fontWeight: "600",
        colors: [theme.labels],
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: ["#818cf8"], // indigo-400, lighter companion
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
        stops: [0, 100],
      },
    },
    colors: [CHART_COLORS.primary],
    series: series || [],
    xaxis: {
      ...base.xaxis,
      categories: categories || [],
    },
    yaxis: {
      ...base.yaxis,
      labels: {
        ...base.yaxis.labels,
        formatter: (val) => {
          if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}k`;

          return `$${val}`;
        },
      },
    },
    tooltip: {
      theme: theme.tooltip,
      y: {
        formatter: (val) => `$${Number(val).toLocaleString()}`,
      },
    },
  };

  return deepMerge(config, overrides);
};

// ---------------------------------------------------------------------------
// Area chart (smooth curve + gradient)
// ---------------------------------------------------------------------------

/**
 * Smooth area chart with gradient fill from primary-500 to transparent.
 */
export const areaChartConfig = (series, categories, overrides = {}) => {
  const theme = getThemeColors();
  const base = baseChartConfig();

  const config = {
    ...base,
    chart: {
      ...base.chart,
      type: "area",
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => {
        if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
        if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}k`;

        return `$${Math.round(val)}`;
      },
      offsetY: -8,
      style: {
        fontSize: "11px",
        fontWeight: "600",
        colors: [theme.labels],
      },
    },
    colors: [CHART_COLORS.primary],
    series: series || [],
    xaxis: {
      ...base.xaxis,
      categories: categories || [],
    },
    yaxis: {
      ...base.yaxis,
      labels: {
        ...base.yaxis.labels,
        formatter: (val) => {
          if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}k`;

          return `$${Math.round(val)}`;
        },
      },
    },
    tooltip: {
      theme: theme.tooltip,
      y: {
        formatter: (val) =>
          `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      },
    },
  };

  return deepMerge(config, overrides);
};

// ---------------------------------------------------------------------------
// Donut chart
// ---------------------------------------------------------------------------

/**
 * Donut chart with 65% inner size, centered total label and bottom legend.
 */
export const donutChartConfig = (series, labels, overrides = {}) => {
  const theme = getThemeColors();

  const total = (series || []).reduce((s, v) => s + v, 0);

  const config = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    labels: labels || [],
    series: series || [],
    colors: CHART_PALETTE,
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "16px",
              fontWeight: 600,
              formatter: () => String(total),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
    },
    legend: { position: "bottom" },
    tooltip: { theme: theme.tooltip },
    responsive: [
      {
        breakpoint: 640,
        options: {
          chart: { height: 260 },
          legend: { fontSize: "11px" },
        },
      },
    ],
  };

  return deepMerge(config, overrides);
};

// ---------------------------------------------------------------------------
// Sparkline (minimal inline chart)
// ---------------------------------------------------------------------------

/**
 * Tiny area chart suitable for inline KPI sparklines.
 * Height 35, no axis, no grid, no labels.
 */
export const sparklineConfig = (data, color = CHART_COLORS.primary) => ({
  chart: {
    type: "area",
    height: 35,
    sparkline: { enabled: true },
    animations: { enabled: false },
  },
  stroke: { curve: "smooth", width: 1.5 },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.05,
      stops: [0, 100],
    },
  },
  colors: [color],
  series: [{ data: data || [] }],
  tooltip: { enabled: false },
  xaxis: { labels: { show: false }, axisBorder: { show: false } },
  yaxis: { labels: { show: false } },
  grid: { show: false },
});

// ---------------------------------------------------------------------------
// Comparison series (current vs previous)
// ---------------------------------------------------------------------------

/**
 * Returns two-series config for period-over-period comparison.
 * Current: solid primary line. Previous: dashed gray line at 30% opacity.
 */
export const comparisonSeriesConfig = (
  currentData,
  previousData,
  currentLabel = "Current Period",
  previousLabel = "Previous Period"
) => [
  {
    name: currentLabel,
    data: currentData || [],
    type: "line",
    color: CHART_COLORS.primary,
  },
  {
    name: previousLabel,
    data: previousData || [],
    type: "line",
    color: CHART_COLORS.gray,
  },
];

/**
 * Stroke overrides for comparison charts. Apply via `stroke` key in options.
 */
export const comparisonStrokeConfig = () => ({
  width: [2, 2],
  curve: "smooth",
  dashArray: [0, 5],
});

/**
 * Opacity overrides for comparison chart fills.
 */
export const comparisonFillConfig = () => ({
  opacity: [1, 0.3],
});

// ---------------------------------------------------------------------------
// Deep merge helper
// ---------------------------------------------------------------------------

/**
 * Simple recursive merge for plain objects. Arrays are replaced, not merged.
 */
function deepMerge(target, source) {
  if (!source || typeof source !== "object") return target;

  const output = { ...target };

  for (const key of Object.keys(source)) {
    const tVal = target[key];
    const sVal = source[key];

    if (
      sVal &&
      typeof sVal === "object" &&
      !Array.isArray(sVal) &&
      tVal &&
      typeof tVal === "object" &&
      !Array.isArray(tVal)
    ) {
      output[key] = deepMerge(tVal, sVal);
    } else {
      output[key] = sVal;
    }
  }

  return output;
}
